/**
 * OfferAIAssessmentModal — instructor-facing 3-step flow to push a
 * one-off AI assessment offer to a pupil (Track A, Phase D).
 *
 * Operator brief (Feb 13, 2026):
 *   "3-step flow:
 *      1. pick assessment type
 *      2. set retail price
 *      3. preview platform fee + instructor margin + send.
 *    Call POST /api/ai-offers/create. On successful create, fire
 *    pupil notification/bell using existing notify() infrastructure
 *    (already wired on the backend)."
 *
 * Designed for Work Mode pupil context: opens as a sheet over the
 * pre-lesson view. Closes on send. Pricing surface reads
 * /api/ai-offers/config so the floor / ceiling / fee always reflect
 * env values (no hardcoded commercial rules).
 *
 * NOT in scope:
 *   • Multi-pupil broadcast.
 *   • Save-as-draft.
 *   • "£X owed" instructor-side strip (parked per operator).
 *   • Telemetry.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, Loader2, Sparkles, ArrowRight, ArrowLeft, Send, CheckCircle,
  Info, AlertTriangle,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { MobileDialog } from "../mobile";
import { MEDIA_FEATURE_ENABLED } from "../../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;


/**
 * Phase D polish (Feb 13, 2026) — localStorage "last-used retail per
 * intent" memory. Per instructor + per assessment type. Auto-fills
 * the previous figure when the instructor reopens the modal; they
 * can still edit freely. Silent convenience feature — no UI flag.
 *
 * Schema:  ai_offer_last_retail_v1[instructorId][intentId] = pence
 *
 * Defensive: any malformed value falls back to the config floor.
 */
const LS_KEY = "ai_offer_last_retail_v1";


function readLastRetail(instructorId, intentId) {
  if (!instructorId || !intentId) return null;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const map = JSON.parse(raw);
    const v = map?.[instructorId]?.[intentId];
    return Number.isInteger(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}


function writeLastRetail(instructorId, intentId, pence) {
  if (!instructorId || !intentId || !Number.isInteger(pence) || pence <= 0) {
    return;
  }
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    const map = raw ? JSON.parse(raw) : {};
    if (!map[instructorId]) map[instructorId] = {};
    map[instructorId][intentId] = pence;
    window.localStorage.setItem(LS_KEY, JSON.stringify(map));
  } catch {
    // ignore quota / private-mode errors silently
  }
}


function poundsFromPence(pence) {
  return ((pence || 0) / 100).toFixed(2);
}


function penceFromInput(value) {
  // Accept "25", "25.0", "25.50"; convert to integer pence; reject NaN.
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}


export default function OfferAIAssessmentModal({
  open,
  pupilId,
  pupilName,
  onClose,
  onSent,
}) {
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState(null);
  const [configErr, setConfigErr] = useState(null);
  const [intent, setIntent] = useState(null);
  const [retailInput, setRetailInput] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const instructorId = user?.id;

  // Body-scroll lock + ESC dismiss handled by <MobileDialog> primitive.

  // Reset internal state every time the modal opens.
  useEffect(() => {
    if (!open) return;
    if (!MEDIA_FEATURE_ENABLED) return;       // Quarantine — no fetch.
    setStep(1);
    setIntent(null);
    setRetailInput("");
    setNote("");
    setSending(false);
    setConfigErr(null);
    (async () => {
      try {
        const r = await axios.get(`${API}/api/ai-offers/config`, {
          withCredentials: true,
        });
        setConfig(r.data);
      } catch (e) {
        setConfigErr(
          e?.response?.data?.detail
            || "Could not load pricing config.",
        );
      }
    })();
  }, [open]);

  // When an intent is picked, auto-fill the retail input with the
  // last-used value for this (instructor, intent) pair, else fall
  // back to the floor. Instructors can still edit freely.
  useEffect(() => {
    if (!intent || !config) return;
    const remembered = readLastRetail(instructorId, intent.id);
    const seedPence = remembered ?? config.retail_min_pence;
    setRetailInput(poundsFromPence(seedPence));
  }, [intent, config, instructorId]);

  if (!open) return null;
  // Media quarantine — silent. No CTA into POST /api/ai-offers/create.
  if (!MEDIA_FEATURE_ENABLED) return null;

  const retailPence = penceFromInput(retailInput);
  const validRetail =
    retailPence !== null
    && config
    && retailPence >= config.retail_min_pence
    && retailPence <= config.retail_max_pence;
  const margin = validRetail
    ? retailPence - (config?.platform_fee_pence || 0)
    : 0;

  const handleSend = async () => {
    if (!validRetail || !intent || !pupilId) return;
    setSending(true);
    try {
      const payload = {
        pupil_user_id: pupilId,
        review_intent: intent.id,
        retail_price_pence: retailPence,
        note: note.trim() || undefined,
      };
      await axios.post(`${API}/api/ai-offers/create`, payload, {
        withCredentials: true,
      });
      // Remember this retail for next time the instructor sends an
      // offer of the same type to any pupil. Silent convenience.
      writeLastRetail(instructorId, intent.id, retailPence);
      toast.success(`Offer sent to ${pupilName || "your pupil"}.`);
      onSent?.();
      onClose?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          || "Could not send. Please try again.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <MobileDialog
      bareContent
      open={open}
      onClose={onClose}
      testid="offer-ai-assessment-modal"
    >
      <div className="w-full max-w-lg bg-slate-950 border border-slate-800 rounded-t-3xl sm:rounded-3xl flex flex-col">
        <header className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-extrabold text-indigo-300 inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Offer AI assessment
            </p>
            <h2 className="text-base font-extrabold text-white">
              {pupilName || "Pupil"}
            </h2>
          </div>
          <button
            type="button"
            data-testid="offer-ai-assessment-close"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-800 active:scale-95 transition"
          >
            <X className="w-4 h-4 text-slate-300" />
          </button>
        </header>

        {configErr ? (
          <div className="p-5">
            <ErrorRow message={configErr} />
          </div>
        ) : !config ? (
          <div className="p-10 flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-indigo-300 animate-spin" />
          </div>
        ) : (
          <>
            <StepPills current={step} />
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {step === 1 && (
                <StepPickIntent
                  config={config}
                  intent={intent}
                  onPick={setIntent}
                />
              )}
              {step === 2 && (
                <StepSetRetail
                  config={config}
                  retailInput={retailInput}
                  onChange={setRetailInput}
                  validRetail={validRetail}
                  margin={margin}
                  note={note}
                  onNoteChange={setNote}
                />
              )}
              {step === 3 && (
                <StepReview
                  pupilName={pupilName}
                  intent={intent}
                  retailPence={retailPence}
                  config={config}
                  margin={margin}
                  note={note}
                />
              )}
            </div>
            <Footer
              step={step}
              canNext={
                step === 1 ? !!intent
                  : step === 2 ? validRetail
                  : true
              }
              sending={sending}
              onBack={() => setStep((s) => Math.max(1, s - 1))}
              onNext={() => setStep((s) => Math.min(3, s + 1))}
              onSend={handleSend}
            />
          </>
        )}
      </div>
    </MobileDialog>
  );
}


// ── Steps ─────────────────────────────────────────────────────────


function StepPills({ current }) {
  return (
    <div className="flex items-center justify-center gap-1.5 px-5 py-3 border-b border-slate-800">
      {[1, 2, 3].map((n) => (
        <div
          key={n}
          data-testid={`offer-ai-step-pill-${n}`}
          className={
            "w-7 h-1.5 rounded-full transition-all "
            + (n <= current ? "bg-indigo-400" : "bg-slate-700")
          }
        />
      ))}
    </div>
  );
}


function StepPickIntent({ config, intent, onPick }) {
  return (
    <div className="space-y-2">
      <p className="text-[12px] text-slate-400 uppercase tracking-widest font-extrabold">
        Step 1 — Pick assessment type
      </p>
      {config.allowed_review_intents.map((it) => {
        const active = intent?.id === it.id;
        return (
          <button
            key={it.id}
            type="button"
            data-testid={`offer-ai-intent-${it.id}`}
            onClick={() => onPick(it)}
            className={
              "w-full text-left rounded-xl p-3 border transition active:scale-[0.99] "
              + (active
                ? "border-indigo-400 bg-indigo-500/15 text-white"
                : "border-slate-700 bg-slate-900/60 text-slate-200 hover:border-slate-600")
            }
          >
            <p className="text-[13.5px] font-extrabold">{it.label}</p>
          </button>
        );
      })}
    </div>
  );
}


function StepSetRetail({
  config, retailInput, onChange, validRetail, margin, note, onNoteChange,
}) {
  const floor = poundsFromPence(config.retail_min_pence);
  const ceil = poundsFromPence(config.retail_max_pence);
  return (
    <div className="space-y-4">
      <p className="text-[12px] text-slate-400 uppercase tracking-widest font-extrabold">
        Step 2 — Set retail price
      </p>
      <div>
        <label className="text-[12px] text-slate-300 font-extrabold flex items-center gap-1">
          Pupil pays
        </label>
        <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/80 px-3 py-2">
          <span className="text-slate-400 text-base font-extrabold">£</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min={floor}
            max={ceil}
            value={retailInput}
            onChange={(e) => onChange(e.target.value)}
            data-testid="offer-ai-retail-input"
            className="flex-1 bg-transparent text-white text-lg font-extrabold focus:outline-none"
            placeholder={floor}
          />
        </div>
        <p
          data-testid="offer-ai-retail-bounds"
          className="text-[11px] text-slate-400 mt-1.5"
        >
          Between £{floor} and £{ceil}.
        </p>
        {!validRetail && retailInput && (
          <div className="mt-2 rounded-lg border border-amber-700/40 bg-amber-900/20 p-2 flex gap-2 items-start">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-300 flex-shrink-0 mt-0.5" />
            <p className="text-[11.5px] text-amber-100">
              Pick a price between £{floor} and £{ceil} to leave a fair margin.
            </p>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-700/60 bg-slate-900/60 p-3 space-y-2">
        <p className="text-[10px] uppercase tracking-widest font-extrabold text-slate-400">
          Margin preview
        </p>
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-slate-300">Platform fee</span>
          <span
            data-testid="offer-ai-fee-preview"
            className="text-slate-300 font-extrabold"
          >
            £{poundsFromPence(config.platform_fee_pence)}
          </span>
        </div>
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-emerald-300 font-extrabold">You earn</span>
          <span
            data-testid="offer-ai-margin-preview"
            className="text-emerald-300 font-extrabold"
          >
            {validRetail ? `£${poundsFromPence(margin)}` : "—"}
          </span>
        </div>
      </div>

      <div>
        <label className="text-[12px] text-slate-300 font-extrabold">
          Note for your pupil <span className="text-slate-500 font-normal">(optional, 240 chars)</span>
        </label>
        <textarea
          rows={3}
          maxLength={240}
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          data-testid="offer-ai-note-input"
          placeholder="e.g. Record a 30-min drive when you're ready."
          className="mt-1.5 w-full rounded-xl border border-slate-700 bg-slate-900/80 px-3 py-2 text-[13px] text-white focus:outline-none focus:border-indigo-500"
        />
      </div>
    </div>
  );
}


function StepReview({
  pupilName, intent, retailPence, config, margin, note,
}) {
  return (
    <div className="space-y-3">
      <p className="text-[12px] text-slate-400 uppercase tracking-widest font-extrabold">
        Step 3 — Review & send
      </p>
      <div className="rounded-xl border border-indigo-700/40 bg-indigo-900/15 p-3 space-y-2">
        <div className="flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5 text-indigo-300" />
          <p className="text-[11px] uppercase tracking-widest font-extrabold text-indigo-300">
            Sending to
          </p>
        </div>
        <p
          data-testid="offer-ai-review-pupil"
          className="text-base text-white font-extrabold"
        >
          {pupilName || "Pupil"}
        </p>
      </div>
      <div className="rounded-xl border border-slate-700/60 bg-slate-900/60 p-3 space-y-2">
        <RowKV
          label="Assessment"
          value={intent?.label}
          testid="offer-ai-review-intent"
        />
        <RowKV
          label="Pupil pays"
          value={`£${poundsFromPence(retailPence)}`}
          testid="offer-ai-review-retail"
        />
        <RowKV
          label="Platform fee"
          value={`£${poundsFromPence(config.platform_fee_pence)}`}
        />
        <RowKV
          label="You earn"
          value={`£${poundsFromPence(margin)}`}
          highlight
          testid="offer-ai-review-margin"
        />
      </div>
      {note ? (
        <div className="rounded-xl border border-slate-700/60 bg-slate-900/60 p-3">
          <p className="text-[10px] uppercase tracking-widest font-extrabold text-slate-400 mb-1.5">
            Note
          </p>
          <p
            data-testid="offer-ai-review-note"
            className="text-[12.5px] text-slate-200 leading-relaxed italic"
          >
            "{note}"
          </p>
        </div>
      ) : null}
    </div>
  );
}


function RowKV({ label, value, highlight, testid }) {
  return (
    <div className="flex items-center justify-between text-[13px]">
      <span className="text-slate-300">{label}</span>
      <span
        data-testid={testid}
        className={
          highlight
            ? "text-emerald-300 font-extrabold"
            : "text-white font-extrabold"
        }
      >
        {value}
      </span>
    </div>
  );
}


function Footer({ step, canNext, sending, onBack, onNext, onSend }) {
  return (
    <div className="px-5 py-4 border-t border-slate-800 flex items-center gap-2">
      {step > 1 ? (
        <button
          type="button"
          data-testid="offer-ai-back-button"
          onClick={onBack}
          className="px-3 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-extrabold flex items-center gap-1.5"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
      ) : (
        <div className="flex-1" />
      )}
      {step < 3 ? (
        <button
          type="button"
          data-testid="offer-ai-next-button"
          disabled={!canNext}
          onClick={onNext}
          className="flex-1 px-4 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 text-white text-sm font-extrabold flex items-center justify-center gap-1.5 active:scale-[0.99] transition"
        >
          Next <ArrowRight className="w-3.5 h-3.5" />
        </button>
      ) : (
        <button
          type="button"
          data-testid="offer-ai-send-button"
          disabled={sending}
          onClick={onSend}
          className="flex-1 px-4 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-slate-900 text-sm font-extrabold flex items-center justify-center gap-1.5 active:scale-[0.99] transition"
        >
          {sending
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <Send className="w-3.5 h-3.5" />}
          Send offer
        </button>
      )}
    </div>
  );
}


function ErrorRow({ message }) {
  return (
    <div className="rounded-xl border border-rose-700/40 bg-rose-900/15 p-3 flex gap-2 items-start">
      <CheckCircle className="w-4 h-4 text-rose-300 mt-0.5 flex-shrink-0" />
      <p className="text-[12.5px] text-rose-100 leading-relaxed">{message}</p>
    </div>
  );
}
